/** @format */

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import Col from 'react-bootstrap/Col'
import './metodoEntrega.css'
import domicilio from '../../Assets/Images/MetodoEntrega/domicilio.svg'
import Direcciones from '../Direcciones'
import Sucursales from '../Sucursales'
import { Shop } from 'react-bootstrap-icons'
import { useDispatch, useSelector } from 'react-redux'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers/'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import es from 'date-fns/locale/es'
import { Button, CircularProgress, TextField, Typography } from '@mui/material'
import DeliveryPlan from './components/DeliveryPlan'
import {
  agregarDireccion,
  agregarSucursal,
  quitarDireccion,
  updatePlanIdLineas,
  updateTemporalFulfillmentPlan,
  updateWarehouseIdLineas,
} from 'redux/actions/cotizacionActions'
import { validatorIsCountedCustomer } from 'Utils/functions'

const detectMethodFromSelectedAddress = selectedAddress => {
  const isHome =
    selectedAddress?.AddressInfo || selectedAddress?.Address_AddressId
  const isBranch =
    selectedAddress?.WarehouseId || selectedAddress?.WarehouseDescription

  if (isHome) return 'domicilio'
  if (isBranch) return 'sucursal'
  return null
}

const MetodoEntrega = ({
  setDireccion,
  setSelectedAddress,
  insideForm,
  selectedAddress,
  direccion,
  validateOnSuccessCallAddress,
  hasDefaultWarehouse,
  client,
  autoClose = true,
  setPromisedDate,
  promisedDate,
  isEditMode = false,
  isQuickSale = false,
  containerClassName = 'delivery-plan-section',
  onCloseMetodoEntrega,
  customerAddress,
}) => {
  const dispatch = useDispatch()

  // --- Estados principales ---
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [dateError, setDateError] = useState(null)
  const [minDateOA, setMinDateOA] = useState(null)
  const [oaLoading, setOaLoading] = useState(false)
  const [selectedHomeDirectionCotization, setSelectedHomeDirectionCotization] =
    useState(null)

  // --- Objeto para guardar datos por método (cache en creación / temporal en edición) ---
  const [methodData, setMethodData] = useState({
    domicilio: {
      address: null,
      date: null,
      minDate: null,
      fulfillmentPlan: null,
    },
    sucursal: {
      address: null,
      date: null,
      minDate: null,
      fulfillmentPlan: null,
    },
  })

  // --- Referencias ---
  const refDireccion = useRef(null)
  const refSucursal = useRef(null)

  // Evitar que effects pisen el método cuando ya se inicializó / o el usuario cambió
  const didInitEditModeRef = useRef(false)
  const userChangedMethodRef = useRef(false)

  // --- Selectores de Redux ---
  const quotationHeader = useSelector(state => state?.cotizacion?.QuoteHeader)
  const cart = useSelector(state => state.cart.cart)
  const temporalFulfillmentPlan = useSelector(
    state => state.cotizacion?.temporalFulfillmentPlan
  )
  const isCountedCustomer = useMemo(
    () => validatorIsCountedCustomer(quotationHeader),
    [quotationHeader?.CustomerId]
  )

  // Usar cart si tiene productos, sino usar quotationHeader.Lines (para EditarCotizacion)
  const productsToCheck = cart.length > 0 ? cart : quotationHeader?.Lines || []
  const hasNonServiceProducts = productsToCheck.some(product =>
    product.IsService !== undefined
      ? !product.IsService
      : product.Department !== 'Servicios'
  )

  const fulfillmentPlanIdSelected = isEditMode
    ? (quotationHeader.Lines || []).filter(item => item.FulfillmentPlanId)?.[0]
        ?.FulfillmentPlanId ?? ''
    : temporalFulfillmentPlan?.fulfillmentPlanId ?? ''

  // -----------------------------
  // Helpers
  // -----------------------------
  const currentMethod = selectedMethod || 'domicilio'

  const updateMethodData = useCallback((method, partial) => {
    if (!method) return
    setMethodData(prev => ({
      ...prev,
      [method]: { ...prev[method], ...partial },
    }))
  }, [])

  const assignWarehouse = useCallback(
    sucursal => {
      const warehouse = {
        WarehouseId: sucursal?.WarehouseId,
        WarehouseDescription: sucursal?.Description,
        WarehouseAddress: sucursal?.WarehouseAddress,
        WarehouseCode: sucursal?.Code,
        WarehouseName: sucursal?.Description,
      }
      dispatch(quitarDireccion())
      dispatch(agregarSucursal(warehouse))
      dispatch(
        updateWarehouseIdLineas({
          WarehouseId: warehouse.WarehouseId,
          WarehouseCode: warehouse.WarehouseCode,
          WarehouseDescription: warehouse.WarehouseDescription,
        })
      )
    },
    [dispatch]
  )

  const handleSucursalSelected = useCallback(
    sucursal => {
      updateMethodData(currentMethod, { address: sucursal })

      // Siempre actualizar el padre para visualización
      setSelectedAddress(sucursal)

      // Solo persistir en Redux en modo creación
      if (!isEditMode) {
        assignWarehouse(sucursal)
      }

      if (!isEditMode && autoClose) onCloseMetodoEntrega?.()
    },
    [
      updateMethodData,
      currentMethod,
      isEditMode,
      setSelectedAddress,
      assignWarehouse,
      autoClose,
      onCloseMetodoEntrega,
    ]
  )

  const handleDireccionSelected = useCallback(
    address => {
      const finalAddress = {
        Address_AddressDetail:
          address.AddressInfo?.Name ||
          address.AddressInfo?.AddressDetail ||
          address.AddressInfo?.Address_AddressDetail,
        Address_AddressId:
          address.AddressInfo?.AddressId ||
          address.AddressInfo?.Address_AddressId,
        AddressId:
          address.AddressInfo?.AddressId ||
          address.AddressInfo?.Address_AddressId,
        Address_AddressTypeId:
          address.AddressInfo?.AddressType ||
          address.AddressInfo?.Address_AddressType,
        Address_BuildingId:
          address.AddressInfo?.BuildingId ||
          address.AddressInfo?.Address_BuildingId,
        Address_BuildingName: null,
        Address_BuildingType:
          address.AddressInfo?.BuildingType ||
          address.AddressInfo?.Address_BuildingType,
        Address_BuildingTypeId:
          address.AddressInfo?.BuildingTypeId ||
          address.AddressInfo?.Address_BuildingTypeId,
        Address_CountryId:
          address.AddressInfo?.Country?.CountryId ||
          address.AddressInfo?.Address_CountryId ||
          address.AddressInfo?.CountryId,
        Address_CountryName:
          address.AddressInfo?.Country?.Name ||
          address.AddressInfo?.Address_CountryName ||
          address.AddressInfo?.CountryName,
        Address_DistrictId:
          address.AddressInfo?.District?.DistrictId ||
          address.AddressInfo?.Address_DistrictId ||
          address.AddressInfo?.DistrictId,
        Address_DistrictName:
          address.AddressInfo?.District?.Name ||
          address.AddressInfo?.Address_DistrictName ||
          address.AddressInfo?.DistrictName,
        Address_HomeOrFloorNumber:
          address.AddressInfo?.HomeOrFloorNumber ||
          address.AddressInfo?.Address_HomeOrFloorNumber ||
          address.AddressInfo?.HomeOrFloorNumber,
        Address_Latitude:
          address.AddressInfo?.Latitude ||
          address.AddressInfo?.Address_Latitude,
        Address_Longitude:
          address.AddressInfo?.Longitude ||
          address.AddressInfo?.Address_Longitude,
        Address_NeighborhoodId:
          address.AddressInfo?.NeighborhoodId ||
          address.AddressInfo?.Address_NeighborhoodId,
        Address_NeighborhoodName:
          address.AddressInfo?.Neighborhood ||
          address.AddressInfo?.Address_NeighborhoodName,
        Address_ProvinceId:
          address.AddressInfo?.Province?.ProvinceId ||
          address.AddressInfo?.Address_ProvinceId ||
          address.AddressInfo?.ProvinceId,
        Address_ProvinceName:
          address.AddressInfo?.Province?.Name ||
          address.AddressInfo?.Address_ProvinceName ||
          address.AddressInfo?.ProvinceName,
        Address_Street:
          address.AddressInfo?.Street ||
          address.AddressInfo?.Address_Street ||
          address.AddressInfo?.AddressDetail,
        Address_TownshipId:
          address.AddressInfo?.Township?.TownshipId ||
          address.AddressInfo?.Address_TownshipId ||
          address.AddressInfo?.TownshipId,
        Address_TownshipName:
          address.AddressInfo?.Township?.Name ||
          address.AddressInfo?.Address_TownshipName ||
          address.AddressInfo?.TownshipName,
        Address_Telephone_Number:
          address.AddressInfo?.Address_Telephone_Number ||
          address.AddressInfo?.PhoneNumber,
        ...(isCountedCustomer && {
          QuoteAddressIdCounted:
            address.AddressInfo?.AddressId ||
            address.AddressInfo?.Address_AddressId,
        }),
      }

      updateMethodData(currentMethod, {
        address: isEditMode ? finalAddress : address,
      })

      setSelectedHomeDirectionCotization(finalAddress)

      // Siempre actualizar el padre para visualización
      setSelectedAddress(isEditMode ? finalAddress : address)

      // Solo persistir en Redux en modo creación
      if (!isEditMode) {
        dispatch(agregarDireccion(finalAddress))
      }

      if (!isEditMode && autoClose) onCloseMetodoEntrega?.()
    },
    [
      setDireccion,
      updateMethodData,
      currentMethod,
      isEditMode,
      setSelectedAddress,
      dispatch,
      autoClose,
      onCloseMetodoEntrega,
    ]
  )

  const handleSetPromisedDate = useCallback(
    date => {
      updateMethodData(currentMethod, { date })
      // Siempre actualizar el padre para visualización
      setPromisedDate(date)
    },
    [currentMethod, setPromisedDate, updateMethodData]
  )

  const handleSetMinDateOA = useCallback(
    minDate => {
      updateMethodData(currentMethod, { minDate })
      setMinDateOA(minDate) // local siempre
    },
    [currentMethod, updateMethodData]
  )

  // ---------------------------------------------
  // Display values (UI)
  // ---------------------------------------------
  const displaySelectedAddress = useMemo(() => {
    return isEditMode ? methodData[currentMethod].address : selectedAddress
  }, [isEditMode, methodData, currentMethod, selectedAddress])

  const displayPromisedDate = useMemo(() => {
    return isEditMode ? methodData[currentMethod].date : promisedDate
  }, [isEditMode, methodData, currentMethod, promisedDate])

  const displayMinDateOA = useMemo(() => {
    return isEditMode ? methodData[currentMethod].minDate : minDateOA
  }, [isEditMode, methodData, currentMethod, minDateOA])

  // Inicializar selectedMethod en edición UNA SOLA VEZ (sin pisar clicks)
  useEffect(() => {
    if (!isEditMode) return
    if (!selectedAddress) return
    if (didInitEditModeRef.current) return
    if (userChangedMethodRef.current) return

    const initial = detectMethodFromSelectedAddress(selectedAddress)
    if (!initial) return

    setSelectedMethod(initial)

    setMethodData(prev => ({
      ...prev,
      [initial]: {
        address: selectedAddress ?? null,
        date: promisedDate ?? null,
        minDate: minDateOA ?? null,
      },
    }))

    didInitEditModeRef.current = true
  }, [isEditMode, selectedAddress, promisedDate, minDateOA])

  useEffect(() => {
    if (isEditMode) return

    // Si ya hay método seleccionado, no lo pises
    if (selectedMethod) return

    // Detectar método desde la dirección actual
    const initial = detectMethodFromSelectedAddress(selectedAddress)

    if (initial) {
      setSelectedMethod(initial)
    } else {
      // Fallback SOLO si no se pudo detectar nada
      setSelectedMethod('domicilio')
    }
  }, [isEditMode, selectedAddress, selectedMethod])

  // --- Handlers de selección de método ---
  const handleMostrarSucursales = useCallback(() => {
    userChangedMethodRef.current = true
    setDireccion(false)

    // Guardar snapshot del método actual (para cache/temporal)
    if (selectedMethod === 'domicilio') {
      updateMethodData('domicilio', {
        address: displaySelectedAddress,
        date: displayPromisedDate,
        minDate: displayMinDateOA,
        fulfillmentPlan: temporalFulfillmentPlan,
      })
    }

    setSelectedMethod('sucursal')

    // Restaurar datos del método sucursal al padre (para visualización)
    const next = methodData.sucursal
    setSelectedAddress(next.address)
    setPromisedDate(next.date)
    setMinDateOA(next.minDate)

    // Restaurar el plan de entrega del método sucursal
    dispatch(updateTemporalFulfillmentPlan(next.fulfillmentPlan))
  }, [
    selectedMethod,
    displaySelectedAddress,
    displayPromisedDate,
    displayMinDateOA,
    temporalFulfillmentPlan,
    updateMethodData,
    methodData,
    setSelectedAddress,
    setPromisedDate,
    dispatch,
  ])

  const handleMostrarDireccion = useCallback(() => {
    userChangedMethodRef.current = true
    setDireccion(true)

    // Guardar snapshot del método actual (para cache/temporal)
    if (selectedMethod === 'sucursal') {
      updateMethodData('sucursal', {
        address: displaySelectedAddress,
        date: displayPromisedDate,
        minDate: displayMinDateOA,
        fulfillmentPlan: temporalFulfillmentPlan,
      })
    }

    setSelectedMethod('domicilio')

    // Restaurar datos del método domicilio al padre (para visualización)
    const next = methodData.domicilio
    setSelectedAddress(next.address)
    setPromisedDate(next.date)
    setMinDateOA(next.minDate)

    // Restaurar el plan de entrega del método domicilio
    dispatch(updateTemporalFulfillmentPlan(next.fulfillmentPlan))
  }, [
    selectedMethod,
    displaySelectedAddress,
    displayPromisedDate,
    displayMinDateOA,
    temporalFulfillmentPlan,
    updateMethodData,
    methodData,
    setSelectedAddress,
    setPromisedDate,
    dispatch,
  ])

  // --- Utilidades para productos y sucursales ---
  const payloadProductSelected = useCallback(() => {
    const tempProducts = cart.map(p => ({ sku: p.Sku, qty: p.Quantity }))
    dispatch({ type: 'SET_PRODUCTS_TO_PICKUP', payload: tempProducts })
  }, [cart, dispatch])

  // --- Validación de fechas ---
  const validateDate = useCallback(
    date => {
      if (!date || !(date instanceof Date) || isNaN(date)) {
        return 'Seleccione una fecha válida'
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const selected = new Date(date)
      selected.setHours(0, 0, 0, 0)

      const minDate = displayMinDateOA ? new Date(displayMinDateOA) : today
      minDate.setHours(0, 0, 0, 0)

      if (selected < minDate) {
        return displayMinDateOA
          ? 'La fecha no puede ser anterior a la fecha mínima sugerida por el sistema'
          : 'La fecha no puede ser anterior a hoy'
      }

      return null
    },
    [displayMinDateOA]
  )

  const handleDateChange = useCallback(
    date => {
      const error = validateDate(date)
      setDateError(error)

      if (error) {
        handleSetPromisedDate(null)
        return
      }

      handleSetPromisedDate(date.toISOString())
      setIsDatePickerOpen(false)
    },
    [validateDate, handleSetPromisedDate]
  )

  // --- Valores calculados con useMemo ---
  const shouldShowDatePicker = useMemo(() => {
    return (
      ((displaySelectedAddress?.AddressInfo || isEditMode) &&
        selectedMethod === 'domicilio') ||
      selectedMethod === 'sucursal'
    )
  }, [displaySelectedAddress, isEditMode, selectedMethod])

  const isButtonDisabled = useMemo(() => {
    // Validaciones comunes
    if (!selectedMethod || oaLoading) return true
    if (hasNonServiceProducts && !temporalFulfillmentPlan) return true

    // Validar que haya una dirección/sucursal seleccionada según el método
    const currentAddress = isEditMode
      ? methodData[currentMethod].address
      : selectedAddress

    if (currentMethod === 'domicilio') {
      const hasValidHomeAddress =
        currentAddress?.AddressInfo || currentAddress?.Address_AddressId
      if (!hasValidHomeAddress) return true
    } else if (currentMethod === 'sucursal') {
      const hasValidBranchAddress =
        currentAddress?.WarehouseId || currentAddress?.WarehouseDescription
      if (!hasValidBranchAddress) return true
    }

    return false
  }, [
    selectedMethod,
    oaLoading,
    hasNonServiceProducts,
    temporalFulfillmentPlan,
    isEditMode,
    methodData,
    currentMethod,
    selectedAddress,
  ])

  useEffect(() => {
    payloadProductSelected()
  }, [payloadProductSelected])

  // --- Botón Continuar (commit real en edición) ---
  const handleContinuar = useCallback(() => {
    if (isEditMode) {
      const data = methodData[currentMethod]
      setSelectedAddress(data.address)
      setPromisedDate(data.date)
      setMinDateOA(data.minDate)

      dispatch(
        updatePlanIdLineas({
          cart,
          fulfillmentPlanId: temporalFulfillmentPlan
            ? temporalFulfillmentPlan.fulfillmentPlanId
            : null,
          fulfillmentPlanDescription: temporalFulfillmentPlan
            ? temporalFulfillmentPlan.fulfillmentPlanDescription
            : null,
        })
      )

      if (currentMethod === 'sucursal') {
        assignWarehouse(data.address)
      } else {
        if (selectedHomeDirectionCotization) {
          dispatch(agregarDireccion(selectedHomeDirectionCotization))
        }
      }
    }

    onCloseMetodoEntrega()
  }, [
    isEditMode,
    methodData,
    currentMethod,
    setSelectedAddress,
    setPromisedDate,
    setMinDateOA,
    temporalFulfillmentPlan,
    dispatch,
    cart,
    assignWarehouse,
    selectedHomeDirectionCotization,
    onCloseMetodoEntrega,
  ])

  return (
    <Col className="h-100">
      <div
        className="metodo-entrega-main"
        style={{ boxShadow: insideForm && 'none' }}>
        {/* --- Header --- */}
        <div
          className="metodo-entrega-header"
          style={{ padding: insideForm && '0' }}>
          <h2>
            {insideForm
              ? 'Elegir método de entrega'
              : 'Selecciona el método de entrega de la compra'}
          </h2>
        </div>

        {/* --- Botones de selección de método --- */}
        <div
          className="metodo-entrega-buttons"
          style={{ padding: insideForm && '0' }}>
          <button
            className={
              selectedMethod === 'domicilio'
                ? 'metodo-entrega-selected'
                : 'button-not-selected'
            }
            data-testid="btn-home-delivery"
            onClick={handleMostrarDireccion}
            ref={refDireccion}>
            <img
              src={domicilio}
              alt="domicilio"
              className={
                selectedMethod === 'domicilio' ? '' : 'img-domicilio-white'
              }
              style={{ width: '1.5vw', height: '1.5vw' }}
            />
            Entrega a domicilio
          </button>

          <button
            className={
              selectedMethod === 'sucursal'
                ? 'metodo-entrega-selected'
                : 'button-not-selected'
            }
            data-testid="btn-branch"
            onClick={handleMostrarSucursales}
            ref={refSucursal}>
            <Shop style={{ width: '1.2vw', height: '1.2vw' }} />
            Retiro en sucursal
          </button>
        </div>

        {/* --- Componente de sucursales --- */}
        {selectedMethod === 'sucursal' && (
          <Sucursales
            insideForm={insideForm}
            selectedAddress={displaySelectedAddress}
            onSucursalSelected={handleSucursalSelected}
          />
        )}

        {/* --- Componente de direcciones --- */}
        {selectedMethod === 'domicilio' && (
          <Direcciones
            setDireccion={setDireccion}
            insideForm={insideForm}
            validateOnSuccessCallAddress={validateOnSuccessCallAddress}
            selectedAddress={displaySelectedAddress}
            autoClose={autoClose}
            setPromisedDate={handleSetPromisedDate}
            companyCode={quotationHeader?.CompanyCode}
            products={cart}
            method={selectedMethod}
            setMinDateOA={handleSetMinDateOA}
            oaLoading={oaLoading}
            setOaLoading={setOaLoading}
            isEditMode={isEditMode}
            onCloseMetodoEntrega={onCloseMetodoEntrega}
            onSelectDirection={handleDireccionSelected}
            customerAddress={customerAddress}
          />
        )}

        {/* --- Date Picker --- */}
        {shouldShowDatePicker && (
          <div className={`metodo-entrega-date-picker ${containerClassName}`}>
            <Typography
              fontWeight={600}
              fontFamily={'Open Sans'}
              my=".5rem"
              variant="body1"
              fontSize="14px">
              Seleccione la fecha de envío
            </Typography>

            <div
              className="date-picker-container"
              data-testid="container-date-picker">
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={es}>
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'Open Sans',
                      fontSize: '14px',
                    },
                    '& .MuiInputAdornment-root > button > svg': {
                      width: '22px',
                      height: '22px',
                    },
                  }}
                  value={
                    displayPromisedDate ? new Date(displayPromisedDate) : null
                  }
                  onChange={handleDateChange}
                  minDate={
                    displayMinDateOA ? new Date(displayMinDateOA) : new Date()
                  }
                  shouldDisableDate={date => {
                    if (!displayMinDateOA) return false
                    const minDate = new Date(displayMinDateOA)
                    minDate.setHours(0, 0, 0, 0)
                    const selectedDate = new Date(date)
                    selectedDate.setHours(0, 0, 0, 0)
                    return selectedDate < minDate
                  }}
                  open={isDatePickerOpen}
                  onOpen={() => setIsDatePickerOpen(true)}
                  onClose={() => setIsDatePickerOpen(false)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      error={!!dateError}
                      helperText={dateError}
                    />
                  )}
                  disabled={!selectedMethod || oaLoading}
                  slotProps={{
                    textField: {
                      inputProps: {
                        'data-testid': 'date-picker-input',
                      },
                    },
                    openPickerButton: {
                      'data-testid': 'date-picker-icon',
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        )}

        {hasNonServiceProducts && (
          <div className={containerClassName}>
            <DeliveryPlan
              isHomeDelivery={selectedMethod === 'domicilio'}
              fulfillmentPlanIdSelected={fulfillmentPlanIdSelected}
            />
          </div>
        )}

        {!isQuickSale && (
          <div
            className={`metodo-entrega-buttons-container ${containerClassName}`}>
            <Button
              variant="contained"
              sx={{ textTransform: 'none' }}
              data-testid="btn-continue-delivery-method"
              onClick={handleContinuar}
              disabled={isButtonDisabled}>
              {oaLoading ? (
                <CircularProgress sx={{ color: '#003082' }} size={20} />
              ) : (
                'Continuar'
              )}
            </Button>
          </div>
        )}
      </div>
    </Col>
  )
}

export default MetodoEntrega
